import { useEffect, useMemo, useState } from 'react';
import {
  AddBoxesRow,
  BoxesColumn,
  BoxesColumnContainer,
  BoxesRowContainer,
  type BoxInformationColumnType,
  FixWidth,
  TableContainer,
  useUnitViewHook,
} from '../../UnitView';
import { CommonUtility } from '@utility/common';
import { useDebouncedEffect } from '@hooks/debounceAsync';
import { ToastMessage } from '@utility/toast';
import { ShipmentViewQuickView } from './quickView';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { PlusIcon } from '@phosphor-icons/react';
import { AppModal } from '@elements/Modal';
import { ItemImage } from '@components/CatalogElements';
import { Row } from '@page-components/shipment/ItemsCommon';
import { DangerText, MuteText } from '@elements/Common';
import { ResponsiveDotts } from '@page-components/dashboard/common';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import type {
  shipmentBoxInformationType,
  ShipmentDynamicFieldType,
} from '@hooks/shipment';

type ComingledBoxItem = {
  title: string;
  sku: string;
  p_sku: string;
  asin: string;
  image: string;
  qty?: number;
  quantity?: number;
};

type ComingledBoxRowProps = {
  index: number;
  isUnitView: boolean;
  imperial: boolean;
  item: ComingledBoxItem;
  boxesInformation: formValueType[];
  addBox: () => void;
  changeBox: (index: number, key: string, value: any) => void;
  columns: BoxInformationColumnType[];
  updatedValue: any;
  inputChanged: (index: number, key: string, value: any) => void;
  errors: errorType;
  showItems: boolean;
};

function ComignledBoxRow({
  index,
  isUnitView,
  imperial,
  item,
  boxesInformation,
  addBox,
  changeBox,
  columns,
  updatedValue,
  inputChanged,
  errors,
  showItems,
}: ComingledBoxRowProps) {
  const maxItems = item.qty || 0;

  const inputtedItems = useMemo(() => {
    let total = 0;
    boxesInformation.forEach(box => {
      if (CommonUtility.isValidNumber(box.items?.[item.sku])) {
        total += Number(box.items?.[item.sku]);
      }
    });
    return total;
  }, [updatedValue, boxesInformation]);

  const onChange = (boxIndex, key, value) => {
    inputChanged(boxIndex, key, value);
  };

  return (
    <>
      {index === 0 && isUnitView && (
        <AddBoxesRow
          boxesInformation={boxesInformation}
          imperial={imperial}
          addBox={addBox}
          changeBox={changeBox}
          columns={columns}
          errors={errors}
        />
      )}
      {showItems && (
        <BoxesRowContainer>
          <FixWidth
            className={`sk:px-5! ${columns[0]?.sticky ? 'sticky' : ''}`}
            width={columns[0]?.width}
            leftPos={columns[0].leftPos}
          >
            <div className="border" />
            <ItemImage
              item={{
                p_images: {
                  display_image: item.image,
                },
              }}
              noMargin
            />
          </FixWidth>
          <FixWidth
            className={`sk:px-5! ${columns[1]?.sticky ? 'sticky' : ''}`}
            width={columns[1]?.width}
            leftPos={columns[1].leftPos}
          >
            <Row>{item.title}</Row>
            <Row>
              <MuteText className="sk:mr-2!">SKU</MuteText>
              <ResponsiveDotts>
                <TextCopyToClipboard text={item.p_sku} iconSize={16} />
              </ResponsiveDotts>
            </Row>
            {item.asin && (
              <Row>
                <MuteText className="sk:mr-2!">ASIN</MuteText>
                <ResponsiveDotts>
                  <TextCopyToClipboard text={item.asin} iconSize={16} />
                </ResponsiveDotts>
              </Row>
            )}
          </FixWidth>
          <FixWidth
            className={`sk:px-5! ${
              columns[2]?.sticky ? 'sticky' : ''
            } flex-center sk:flex-col!`}
            width={columns[2]?.width}
            leftPos={columns[2].leftPos}
          >
            {inputtedItems}/{maxItems}
            {(!!(inputtedItems > maxItems) ||
              errors[`box-${index}`]?.itemsError) && (
              <DangerText className="sk:text-center">
                {errors[`box-${index}`]?.itemsError ||
                  'The selected quantity exceeds the maximum allowable units'}
              </DangerText>
            )}
          </FixWidth>
          {!!boxesInformation.length && (
            <FixWidth
              className={`sk:px-5! ${columns[3]?.sticky ? 'sticky' : ''}`}
              width={columns[3]?.width}
              leftPos={columns[3].leftPos}
            />
          )}
          {boxesInformation?.map((box, boxIndex) => (
            <FixWidth
              width={columns[4 + boxIndex]?.width}
              className="flex-center sk:px-5!"
            >
              <div className="sk:w-17.5">
                <NumberMaskInput
                  value={updatedValue?.[boxIndex]?.items?.[item.sku]}
                  onChange={value => onChange(boxIndex, item.sku, value)}
                />
              </div>
            </FixWidth>
          ))}
        </BoxesRowContainer>
      )}
    </>
  );
}

type ShippingBoxValue = {
  dimensions: {
    length: number;
    width: number;
    height: number;
    unitOfMeasurement: string;
  };
  weight: {
    value: number;
    unit: string;
  };
  quantity: number;
  items: {
    msku: string;
    quantity: number;
  }[];
};

type ShippingBoxPopupProps = {
  value: ShippingBoxValue[];
  item: ShipmentDynamicFieldType;
  onChange: (name: string, value: any) => void;
  showItems: boolean;
  isFBA?: boolean;
  rowValue: any;
};

type BoxType = {
  dimensions: {
    length: number;
    width: number;
    height: number;
    unitOfMeasurement: string;
  };
  weight: {
    value: number;
    unit: string;
  };
};

type ItemBox = BoxType & {
  quantity?: number;
  items?: {
    msku: string;
    quantity: number;
  }[];
};

type formValueType = shipmentBoxInformationType & {
  items?: {
    [sku: string]: number;
  };
};

type errorType = Record<string, Record<string, string>>;

function camelCaseToTitle(text) {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase());
}

const errorObject = (
  formValue: formValueType[],
  isFBA?: boolean,
  showItems?: boolean,
) => {
  const errors: errorType = {};

  formValue?.forEach((box, index) => {
    errors[`box-${index}`] = {};

    if (showItems) {
      Object.values(box.items || {}).forEach(val => {});
      const totalItemsInBox = Object.values(box.items || {}).reduce(
        (total, qty) => total + Number(qty || 0),
        0,
      );

      if (totalItemsInBox === 0) {
        errors[`box-${index}`].itemsError =
          'At least one item must be added to the box';
      }
    }

    for (const boxProperty in box) {
      if (
        !['boxGrossWeight', 'boxHeight', 'boxLength', 'boxWidth'].includes(
          boxProperty,
        )
      ) {
        continue;
      }

      if (isFBA) {
        const newValue = box[boxProperty]?.value;
        if (!newValue) {
          errors[`box-${index}`][boxProperty] =
            `${camelCaseToTitle(boxProperty)} is required`;

          continue;
        }

        const dims = [
          box.boxHeight?.value || 0,
          box.boxLength?.value || 0,
          box.boxWidth?.value || 0,
        ].sort((a, b) => b - a);

        if (dims[0] > 36 || dims[1] > 25 || dims[2] > 25) {
          errors[`box-${index}`].boxError =
            "Box exceeds Amazon's 25x25x36 inch limit for standard-size units";
        }

        continue;
      }

      const newValue = box[boxProperty]?.value;
      if (!newValue) {
        errors[`box-${index}`][boxProperty] =
          `${camelCaseToTitle(boxProperty)} is required`;

        continue;
      }

      if (boxProperty === 'boxGrossWeight') {
        if (newValue > 1000) {
          errors[`box-${index}`][boxProperty] =
            `${camelCaseToTitle(boxProperty)} must be less than 1000 lb.`;
        }

        continue;
      }

      if (newValue > 200) {
        errors[`box-${index}`][boxProperty] =
          `${camelCaseToTitle(boxProperty)} must be less than 200 in.`;
      }
    }

    if (!CommonUtility.isValidObject(errors[`box-${index}`])) {
      delete errors[`box-${index}`];
    }
  });

  return errors;
};

export function ShippingBoxPopup({
  value,
  item,
  onChange,
  showItems,
  rowValue,
  isFBA,
}: ShippingBoxPopupProps) {
  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState<formValueType[]>([]);
  const [errors, setErrors] = useState<errorType>({});
  const [submitted, setSubmitted] = useState(false);

  const emptyBox: shipmentBoxInformationType = {
    boxGrossWeight: {
      value: 0,
      unit: 'lb',
    },
    boxHeight: {
      value: 0,
      unit: 'in',
    },
    boxLength: {
      value: 0,
      unit: 'in',
    },
    boxWidth: {
      value: 0,
      unit: 'in',
    },
  };

  const closePopup = () => {
    setOpen(false);
  };

  const addBox = () => {
    const arr = [...formValue];
    arr.push(emptyBox);
    setFormValue(arr);
  };

  useEffect(() => {
    if (CommonUtility.isValidArray(value)) {
      const newFormValue: formValueType[] = value?.map(box => ({
        boxGrossWeight: {
          unit: box.weight?.unit,
          value: box.weight?.value,
        },
        boxHeight: {
          value: box.dimensions?.height,
          unit: box.dimensions?.unitOfMeasurement,
        },
        boxLength: {
          value: box.dimensions?.length,
          unit: box.dimensions?.unitOfMeasurement,
        },
        boxWidth: {
          value: box.dimensions?.width,
          unit: box.dimensions?.unitOfMeasurement,
        },
      }));

      setFormValue(JSON.parse(JSON.stringify(newFormValue)));
    } else {
      setFormValue([]);
    }
    setSubmitted(false);
    setErrors({});
  }, [value, open]);

  const duplicateBoxColumn = (index: number) => {
    const newValue = [...formValue];
    const box = JSON.parse(JSON.stringify(newValue[index]));
    newValue.push(box);
    setFormValue(newValue);
  };

  const deleteBoxColumn = (index: number) => {
    let newValue = [...formValue];
    newValue = newValue.filter((x, i) => i !== index);
    setFormValue(newValue);
  };

  const { columns } = useUnitViewHook({
    boxesInformation: formValue,
    deleteBoxColumn,
    duplicateBoxColumn,
  });

  const parsedItems: ComingledBoxItem[] = useMemo(() => {
    // @ts-expect-error Argument of type 'string | undefined' is not assignable to parameter of type 'unknown[]
    if (CommonUtility.isValidObject(item.list) && rowValue) {
      if (item.list[rowValue.shipmentId]) {
        return item.list[rowValue.shipmentId].map(x => ({
          ...x,
          p_sku: x.sku,
          qty: x.quantity,
        }));
      }
    }
    if (Array.isArray(item.list)) {
      return item.list.map(x => ({
        ...x,
        p_sku: x.sku,
        qty: x.quantity,
      }));
    }
    return [];
  }, [item, rowValue]);

  useDebouncedEffect(
    () => {
      if (!CommonUtility.isValidArray(value)) {
        onChange(item.name, [emptyBox]);
      }
    },
    [value],
    500,
  );

  const changeBox = (index: number, key: string, val: any) => {
    const newValue = [...formValue];

    if (newValue[index][key]) {
      newValue[index][key].value = val;
    } else if (newValue[index]) {
      newValue[index][key] = {
        value: val,
        unit: key.includes('weight') ? 'lb' : 'in',
      };
    } else {
      newValue[index] = {
        [key]: {
          value: val,
          unit: key.includes('weight') ? 'lb' : 'in',
        },
      };
    }

    setFormValue(newValue);
    if (submitted) {
      const newErrors = errorObject(formValue, isFBA, showItems);
      setErrors(newErrors);
    }
  };

  const changeBoxItem = (index: number, key: string, val: any) => {
    const newValue = [...formValue];
    if (newValue[index]?.items) {
      newValue[index].items[key] = val;
    } else {
      newValue[index].items = { [key]: val };
    }
    setFormValue(newValue);
  };

  const totalItems = useMemo(() => {
    if (CommonUtility.isValidArray(value)) {
      return value.length;
    }
    return 0;
  }, [value]);

  const onConfirm = () => {
    const newErrors = errorObject(formValue, isFBA, showItems);
    if (CommonUtility.isValidObject(newErrors)) {
      setSubmitted(true);
      setErrors(newErrors);

      ToastMessage.error('Kindly resolve errors');
    } else {
      const payload: ItemBox[] = formValue.map(box => {
        const newBox: ItemBox = {
          dimensions: {
            length: Number(box.boxLength?.value || 0),
            width: Number(box.boxWidth?.value || 0),
            height: Number(box.boxHeight?.value || 0),
            unitOfMeasurement: 'in',
          },

          weight: {
            value: Number(box.boxGrossWeight?.value || 0),
            unit: 'lb',
          },
        };

        if (showItems) {
          newBox.items = Object.keys(box.items || {})?.map(sku => ({
            msku: sku,
            quantity: box.items?.[sku] || 0,
          }));

          newBox.quantity = newBox.items?.reduce(
            (total, newBoxItem) => total + Number(newBoxItem.quantity || 0),
            0,
          );
        }

        return newBox;
      });

      onChange(item.name, payload);
      closePopup();
    }
  };

  return (
    <>
      {totalItems > 0 ? (
        <ShipmentViewQuickView
          totalItems={totalItems}
          openPopup={() => setOpen(true)}
          items={parsedItems}
          value={value}
        />
      ) : (
        <HoverBorderButton
          onClick={() => setOpen(true)}
          className="flex-btn sk:p-1!"
        >
          <PlusIcon />
        </HoverBorderButton>
      )}

      <AppModal
        open={open}
        size="large"
        closeModal={closePopup}
        title="Add Box Information"
        footer={<StyledButton onClick={onConfirm}>Confirm</StyledButton>}
      >
        <div className="sk:mb-4">
          <b>Boxes</b>
          <TableContainer className="no-shadow">
            <BoxesColumnContainer>
              {columns?.map(column => (
                <BoxesColumn column={column} />
              ))}
            </BoxesColumnContainer>
            {parsedItems?.map((parseItem: ComingledBoxItem, index) => (
              <ComignledBoxRow
                isUnitView
                boxesInformation={formValue}
                imperial
                addBox={addBox}
                changeBox={changeBox}
                item={parseItem}
                index={index}
                columns={columns}
                inputChanged={changeBoxItem}
                updatedValue={formValue}
                errors={errors}
                showItems={showItems}
              />
            ))}
          </TableContainer>
        </div>
      </AppModal>
    </>
  );
}
